import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { makeStyles } from '@material-ui/styles';
import ReactQuill, { Quill } from 'react-quill';
import { uploadUserImage } from 'utils/inbox';
import ImageResize from 'quill-image-resize-module';
import { dispatchError } from 'redux/actions/messageActions';
import { partialUpdateEmailConfig } from 'redux/actions/emailConfigActions';
import NotificationSystem from 'rc-notification';
import { first, isEmpty, isFunction, isNil } from 'lodash';
import * as Emoji from 'quill-emoji';
import { ReactComponent as SignatureIcon } from 'icons/signatureIcon.svg';
import IndentStyle from './indent';
import BasicNotification from '../Notification';
import { NOTIFICATION_NODE_STYLE } from '../../constants/misc';
import { fontsList, sizesList } from '../../constants/pitch';
import { secondary100 } from '../../themes/colors';
import {
  customColorAttributor,
  normalFontSizeAttributor,
  customFontSizeAttributor,
  // CustomListItem,
} from './CustomAttributors';
import { alignCustomIcon, centerAlignCustomIcon, justifyAlignCustomIcon, rightAlignCustomIcon } from './customIcons';
import EmailSignatureModal from '../modals/EmailSignatureModal';

const Block = Quill.import('blots/block');
Block.tagName = 'DIV';

const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = sizesList;

const FontStyle = Quill.import('attributors/style/font');
FontStyle.whitelist = fontsList;

const FontClass = Quill.import('attributors/class/font');
FontClass.whitelist = fontsList;

const AlignStyle = Quill.import('attributors/style/align');

Quill.register(Block, true);
Quill.register(IndentStyle, true);
Quill.register('modules/imageResize', ImageResize);
Quill.register(Emoji, true);
Quill.register(SizeStyle, true);
Quill.register(FontStyle, true);
Quill.register(FontClass, true);
Quill.register(AlignStyle, true);
// Custom List attributors to add styles to the <li> tags.
// Quill.register(CustomListItem);
Quill.register(customColorAttributor);
// customFontSizeAttributor used for applying font size to the <li> tag.
// But this item produces the bug with applying other styles to the text.
// Redefining normalFontSizeAttributor above solves the issue.
// More specific the issue described here - https://github.com/quilljs/quill/issues/2687#issuecomment-516026058
Quill.register(customFontSizeAttributor);
Quill.register(normalFontSizeAttributor);

// Set custom toolbar button's icons.
const icons = Quill.import('ui/icons');
// eslint-disable-next-line dot-notation
icons.align[''] = alignCustomIcon;
icons.align.justify = justifyAlignCustomIcon;
icons.align.center = centerAlignCustomIcon;
icons.align.right = rightAlignCustomIcon;

const Link = Quill.import('formats/link');

class CustomLink extends Link {
  static get urlReg() {
    return /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_.~#?&//=]*)/;
  }
  static create(value) {
    const node = super.create(value);
    node.setAttribute('href', value);
    if (!this.urlReg.test(value)) {
      node.removeAttribute('href');
    } else {
      node.setAttribute('target', '_blank');
    }
    return node;
  }
  // eslint-disable-next-line consistent-return
  static sanitize(url) {
    const note = {};
    // eslint-disable-next-line no-return-assign
    NotificationSystem.newInstance({ style: { zIndex: '9999' } }, n => note.notification = n);
    const value = super.sanitize(url);
    if (value) {
      // eslint-disable-next-line no-useless-escape
      if (!this.urlReg.test(value) && !isEmpty(note)) {
        note.notification.notice({
          content: <BasicNotification
            color="danger"
            message="Invalid URL - include http:// prefix"
          />,
          duration: 6,
          closable: false,
          style: NOTIFICATION_NODE_STYLE,
          className: 'alert--colored right-up',
        });
      }
      return value;
    }
  }
  format(name, value) {
    super.format(name, value);

    if (name !== this.statics.blotName || !value) {
      return;
    }
    if (!this.constructor.urlReg.test(value)) {
      this.domNode.removeAttribute('href');
    } else {
      this.domNode.setAttribute('target', '_blank');
    }
  }
}
Quill.register(CustomLink);

const useStyles = makeStyles(theme => ({
  quill: props => ({
    color: 'black',
    fontFamily: theme.typography.fontFamily,
    '& .ql-container': {
      height: '100% !important',
      border: `1px solid ${theme.palette.secondary['300']} !important`,
      borderRadius: '0.375rem',
      overflowY: 'auto',
      resize: 'none',
      minHeight: props.isSignatureModal ? '8.5rem' : '25vh',
      '& .ql-editor': {
        height: '100%',
        minHeight: props.isSignatureModal ? '8.5rem' : '25vh',
        '& li::before': {
          display: 'inline',
        },
      },
      fontFamily: 'Sans-Serif',
      '& .ql-tooltip': {
        left: 'unset !important', // fixing bug https://github.com/quilljs/quill/issues/360
      },
    },
    '& .ql-toolbar': {
      border: '0 !important',
      maxHeight: '6rem',
      '& .ql-picker-label': {
        padding: '0 3px !important',
      },
    },
    '& .ql-blank::before': {
      opacity: '1 !important',
      color: `${theme.palette.gray.contrastText} !important`,
      fontSize: '0.875rem',
    },
    '& .ql-picker.ql-size': {
      // Font size is 14px for '.ql-picker-label' by intention
      // To keep toolbar look consistent.
      '& .ql-picker-label': {
        '&[data-value="10px"]::before': {
          content: '"Small"',
          fontSize: '14px',
        },
        '&[data-value="32px"]::before': {
          content: '"Huge"',
          fontSize: '14px',
        },
      },
      '& .ql-picker-item': {
        '&[data-value="10px"]::before': {
          content: '"Small" !important',
          fontSize: '10px',
        },
        '&[data-value="32px"]::before': {
          content: '"Huge" !important',
          fontSize: '32px',
        },
      },
    },
    '& .ql-picker.ql-font': {
      width: '140px !important',
      fontSize: '14px',
      '& .ql-picker-item, .ql-picker-label.ql-active': {
        '&[data-value="Montserrat"]::before': {
          fontFamily: 'Montserrat',
          content: '"Montserrat" !important',
        },
        '&[data-value="Rubik"]::before': {
          fontFamily: 'Rubik',
          content: '"Rubik" !important',
        },
        '&[data-value="Arial"]::before': {
          fontFamily: 'Arial',
          content: '"Arial" !important',
        },
        '&[data-value="Georgia"]::before': {
          fontFamily: 'Georgia',
          content: '"Georgia" !important',
        },
        '&[data-value="Courier"]::before': {
          fontFamily: 'Courier',
          content: '"Courier" !important',
        },
        '&[data-value="Bradley Hand"]::before': {
          content: '"Bradley Hand" !important',
          fontFamily: 'Bradley Hand',
          fontSize: '12px !important',
        },
      },
    },
    '& .ql-formats': {
      '& button:hover, & span.ql-picker-label:hover, & .ql-active, & span.ql-picker-item:hover, & .ql-selected': {
        color: `${theme.palette.secondary['900']} !important`,
        '& .ql-stroke:not(.ql-color-label)': {
          stroke: `${theme.palette.secondary['900']} !important`,
        },
        '& .ql-fill': {
          fill: `${theme.palette.secondary['900']} !important`,
        },
      },
    },
  }),
  previewQuill: {
    color: 'black',
    fontFamily: theme.typography.fontFamily,
    '& .ql-toolbar': {
      border: 'none !important',
      padding: '0 !important',
      borderBottom: `1px solid ${secondary100} !important`,
      paddingBottom: '1rem !important',
    },
    '& .ql-container': {
      border: 'none !important',
      height: '16.625rem !important',
      '& .ql-editor': {
        '& li::before': {
          display: 'inline',
        },
        maxHeight: '16.125rem !important',
        marginTop: '1.5rem !important',
        paddingTop: '0 !important',
      },
    },
    '& .ql-blank::before': {
      opacity: '1 !important',
      color: `${theme.palette.gray.contrastText} !important`,
      fontSize: '0.875rem',
    },
    '& .ql-picker.ql-size': {
      // Font size is 14px for '.ql-picker-label' by intention
      // To keep toolbar look consistent.
      '& .ql-picker-label': {
        '&[data-value="10px"]::before': {
          content: '"Small"',
          fontSize: '14px',
        },
        '&[data-value="32px"]::before': {
          content: '"Huge"',
          fontSize: '14px',
        },
      },
      '& .ql-picker-item': {
        '&[data-value="10px"]::before': {
          content: '"Small" !important',
          fontSize: '10px',
        },
        '&[data-value="32px"]::before': {
          content: '"Huge" !important',
          fontSize: '32px',
        },
      },
    },
    '& .ql-picker.ql-font': {
      width: '140px !important',
      fontSize: '14px',
      '& .ql-picker-item, .ql-picker-label.ql-active': {
        '&[data-value="Montserrat"]::before': {
          fontFamily: 'Montserrat',
          content: '"Montserrat" !important',
        },
        '&[data-value="Rubik"]::before': {
          fontFamily: 'Rubik',
          content: '"Rubik" !important',
        },
        '&[data-value="Arial"]::before': {
          fontFamily: 'Arial',
          content: '"Arial" !important',
        },
        '&[data-value="Georgia"]::before': {
          fontFamily: 'Georgia',
          content: '"Georgia" !important',
        },
        '&[data-value="Courier"]::before': {
          fontFamily: 'Courier',
          content: '"Courier" !important',
        },
        '&[data-value="Bradley Hand"]::before': {
          content: '"Bradley Hand" !important',
          fontFamily: 'Bradley Hand',
          fontSize: '12px !important',
        },
      },
    },
    '& .ql-formats': {
      '& button:hover, & span.ql-picker-label:hover, & .ql-active, & span.ql-picker-item:hover, & .ql-selected': {
        color: `${theme.palette.secondary['900']} !important`,
        '& .ql-stroke:not(.ql-color-label)': {
          stroke: `${theme.palette.secondary['900']} !important`,
        },
        '& .ql-fill': {
          fill: `${theme.palette.secondary['900']} !important`,
        },
      },
    },
  },
}));

const WrappedQuill = (props) => {
  const {
    email = '',
    uploading,
    setUploading,
    isPreviewQuill,
    handleOnChangeImg,
    handleOnChange,
    attachRef,
    signature = '',
    emailServiceProviderConfigId = null,
    // eslint-disable-next-line no-unused-vars
    isSignatureModal = false,
  } = props;
  const [initialSignature, setInitialSignature] = useState(signature);
  const [showEditSignature, setShowEditSignature] = useState(false);
  const classes = useStyles(props);

  useEffect(() => {
    document.querySelectorAll('.ql-picker').forEach((tool) => {
      tool.addEventListener('mousedown', (event) => {
        event.preventDefault();
      });
    });
  }, []);

  const handleChange = async (value, delta, source, editor) => {
    const { allDeltas, error } = await uploadUserImage(delta, source, editor, setUploading);
    if (error) {
      dispatchError(error);
    }
    if (allDeltas) {
      handleOnChangeImg(allDeltas, editor);
    } else if (source === 'user' && isFunction(handleOnChange)) {
      const valueClone = value;
      handleOnChange(valueClone);
    }
  };

  const hasSignatureFeature = first(document.getElementsByClassName('ql-formats')) && !isNil(email) && email !== '';

  const handleSignatureEdit = async (newSignature) => {
    await props.partialUpdateEmailConfig(emailServiceProviderConfigId, { signature: newSignature });
    setInitialSignature(newSignature);
    setShowEditSignature(false);
  };

  return (
    <>
      <ReactQuill
        {...props}
        className={isPreviewQuill ? classes.previewQuill : classes.quill}
        ref={el => attachRef(el)}
        readOnly={uploading}
        onChange={(newValue, delta, source, editor) => {
          handleChange(newValue, delta, source, editor);
        }}
      />
      {hasSignatureFeature && createPortal(
        <button onClick={() => setShowEditSignature(true)}><SignatureIcon /></button>,
        first(document.getElementsByClassName('ql-formats')),
      )}
      <EmailSignatureModal
        open={showEditSignature}
        onBackdropClick={() => setShowEditSignature(false)}
        onCancel={() => setShowEditSignature(false)}
        onConfirm={handleSignatureEdit}
        email={email}
        initialSignature={initialSignature}
      />
    </>
  );
};

export default compose(connect(
  null,
  { dispatchError, partialUpdateEmailConfig },
))(WrappedQuill);
